'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

interface MarqueeSettings {
  text: string;
  is_enabled: boolean;
  speed_seconds: number;
}

const DEFAULT_TEXT =
  '🏠 Tolettu.com — Find Your Perfect Rental Home | 🏢 Rent Commercial Spaces | 📍 Find Properties Near You | 🤝 List Your Property & Find Tenants Easily | 🔑 Simple • Fast • Trusted Rentals';

export default function MarqueeBar() {
  const [settings, setSettings] = useState<MarqueeSettings>({
    text: DEFAULT_TEXT,
    is_enabled: true,
    speed_seconds: 28,
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('marquee_settings')
      .select('text, is_enabled, speed_seconds')
      .limit(1)
      .single()
      .then(({ data }) => {
        if (data) {
          setSettings({
            text: data.text || DEFAULT_TEXT,
            is_enabled: data.is_enabled ?? true,
            speed_seconds: data.speed_seconds || 28,
          });
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  if (!loaded || !settings.is_enabled) return null;

  const duration = `${settings.speed_seconds}s`;

  return (
    <>
      <style>{`
        @keyframes marquee-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .marquee-track {
          display: flex;
          width: max-content;
          animation: marquee-scroll ${duration} linear infinite;
          will-change: transform;
        }

        .marquee-wrapper:hover .marquee-track {
          animation-play-state: paused;
        }

        .marquee-static {
          display: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .marquee-track {
            animation: none !important;
            width: 100%;
            overflow: hidden;
          }
          .marquee-track span:last-child {
            display: none;
          }
          .marquee-static {
            display: flex;
          }
          .marquee-animated {
            display: none;
          }
        }
      `}</style>

      <div
        className="marquee-wrapper w-full overflow-hidden bg-white border-t border-b border-gray-200 shadow-sm"
        style={{ height: 'clamp(36px, 5vw, 50px)' }}
        aria-label="Announcement bar"
      >
        {/* Reduced-motion: static centered text */}
        <div className="marquee-static w-full h-full items-center justify-center px-4 text-sm font-medium text-purple-700 truncate">
          {settings.text}
        </div>

        {/* Animated marquee — two copies for seamless loop */}
        <div className="marquee-animated w-full h-full overflow-hidden">
          <div className="marquee-track h-full items-center" aria-hidden="true">
            {[0, 1].map((i) => (
              <span
                key={i}
                className="flex items-center h-full px-8 text-sm font-medium text-purple-700 whitespace-nowrap"
              >
                {settings.text}
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
